/**
 * userAvatar.js — Iter402-P5
 * --------------------------
 * Unified avatar resolver for user objects across the app. Many users who
 * registered as provider / realtor / gallery_dealer never set a personal
 * `picture` but DO have a business `logo_url`. Before this helper, the
 * navbar / header / profile page displayed an empty "K" fallback because
 * those surfaces only read `user.picture`.
 *
 * Priority:
 *   1. public_profile_display === 'business' AND logo_url → logo_url
 *   2. picture (personal avatar)
 *   3. logo_url (business fallback for any user missing a personal avatar)
 *   4. null (caller should render initials fallback)
 *
 * The returned URL is NOT prefixed — callers that already normalize
 * `/api/...` → `${API}/...` handle that themselves.
 */
export function resolveUserAvatar(user) {
  if (!user) return null;
  const picture = user.picture || null;
  const logo = user.logo_url || null;
  const display = user.public_profile_display || 'personal';

  if (display === 'business' && logo) return logo;
  if (picture) return picture;
  if (logo) return logo;
  return null;
}

/**
 * Prefix /api/... paths with the backend URL. Pass-through for full URLs.
 */
export function normalizeAvatarUrl(url) {
  if (!url) return null;
  if (typeof url !== 'string') return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  if (url.startsWith('/')) return `${base}${url}`;
  return url;
}

/** One-shot helper: resolve + normalize. */
export function getUserAvatarUrl(user) {
  return normalizeAvatarUrl(resolveUserAvatar(user));
}
